import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { useEffect, useState } from "react";
import { isEmpty } from "../../../utils/Util";

import { TargetPayApi } from "../../../utils/UtilTarget";
import DealerLocationGroup from "./DealerLocationGroup";
import { useLocation } from "react-router-dom";
import { IDealerLocationGroup } from "../../../types/types";

export default function ManageDealerLocationGroup() {
  const location = useLocation();
  const dealerData = location?.state?.DealerSummary;
  const dmsDealerId = dealerData?.dmsDealerId;
  const companyDealerId = dealerData?.companyDealerId;

  const [dealerLocationGroups, setDealerLocationGroups] = useState<
    IDealerLocationGroup[]
  >([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [
    selectedGroup,
    setSelectedGroup
  ] = useState<IDealerLocationGroup | null>(null);

  const toggleDrawer = (groupData?: IDealerLocationGroup | null) => {
    setSelectedGroup(
      groupData
        ? { ...groupData }
        : {
            dmsDealerId,
            companyDealerId,
            locationGroupId: 0,
            name: "",
            description: ""
          }
    );
    setIsDrawerOpen(!isDrawerOpen);
  };

  useEffect(() => {
    document.title = "Setup Location Group";
    if (isEmpty(dmsDealerId)) return;

    const fetchDealerLocationGroups = async () => {
      try {
        setIsProcessing(true);

        await GetDealerLocationGroups(dmsDealerId);
      } catch (error) {
        console.error("Failed to fetch location groups", error);
      } finally {
        setIsProcessing(false);
      }
    };

    fetchDealerLocationGroups();
  }, [dmsDealerId]);

  // Get Dealer Location Groups
  const GetDealerLocationGroups = async (dmsDealerId: number) => {
    const res = await TargetPayApi(
      `GetDealerLocationGroups/${dmsDealerId}/${companyDealerId}`,
      null,
      "get",
      "Dealer"
    );

    setDealerLocationGroups(res || []);
  };

  const handleSave = (model: IDealerLocationGroup, isEditMode: boolean) => {
    setDealerLocationGroups(prev =>
      isEditMode
        ? prev.map(g =>
            g.locationGroupId === model.locationGroupId ? model : g
          )
        : [...prev, model]
    );

    setIsDrawerOpen(false);
  };

  return (
    <Box>
      {isProcessing ? (
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress size={25} />
        </Box>
      ) : (
        <>
          <Box display="flex" sx={{ fontSize: "small", mb: 1 }}>
            <Box sx={{ ml: "auto" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => toggleDrawer(null)}
              >
                Create
              </Button>
            </Box>

            {isDrawerOpen && (
              <DealerLocationGroup
                open={isDrawerOpen}
                onClose={() => toggleDrawer(null)}
                onSaveDealerLocationGroup={handleSave}
                dmsDealerId={dmsDealerId}
                locationGroupData={selectedGroup}
              />
            )}
          </Box>

          <Paper>
            <TableContainer
              sx={{ height: "35vh", overflow: "auto", scrollbarWidth: "thin" }}
            >
              <Table
                size="small"
                className="sticky-header"
                sx={{ tableLayout: "fixed", width: "100%" }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Group Name</TableCell>
                    <TableCell>Group Description</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dealerLocationGroups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3}>No record found.</TableCell>
                    </TableRow>
                  ) : (
                    dealerLocationGroups.map(group => (
                      <TableRow key={group.locationGroupId}>
                        <TableCell
                          sx={{
                            whiteSpace: "normal",
                            wordWrap: "break-word"
                          }}
                        >
                          {group.name}
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: "normal",
                            wordWrap: "break-word"
                          }}
                        >
                          {group.description}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="text"
                            color="primary"
                            sx={{
                              padding: 0,
                              minWidth: "auto",
                              textTransform: "none",
                              "&:hover": {
                                textDecoration: "underline",
                                backgroundColor: "transparent"
                              }
                            }}
                            onClick={() => toggleDrawer(group)}
                          >
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
}
